import { JSX } from 'solid-js';
import {
  Location,
  RouteMatch,
  useIsRouting,
  RouteDefinition as BaseRouteDefinition,
  useCurrentMatches,
} from '@solidjs/router';

import { Action, getAction, LocationItem, snapeLocation } from './StaticRouter';
import { shallow } from '../../utils';

const MAX_CACHE = 40;
export const defaultKey = Symbol('default') as RouteDefinition;

export const Context = createContext<RouterContext>(null as unknown as RouterContext);
export function useRouterStacks() {
  return useContext(Context);
}
export function useCloseDialog() {
  return useRouterStacks().close;
}

export interface RouterContext {
  index: number;
  current: StackProps;
  root: StackProps;
  stacks: StackProps[];
  action: Action;
  close(cb?: any): void;
}

export interface StackProps extends LocationItem {
  dialog: boolean;
  key: RouteDefinition;
  children: RouteDefinition[];
  matches: RouteDefinition[];
  setState(state: LocationItem): void;
}

export function StackContext(props: {
  location: Location;
  baseRoutes: RouteDefinition[];
  children: JSX.Element;
  beforeRoute(match: RouteDefinition, location: Location): string | false;
}) {
  const { location, baseRoutes } = props;
  const navigate = useNavigate();

  const matches = useCurrentMatches();

  const isDialog = createMemo(
    () => {
      return Boolean((matches()[0]?.route.key as any)?.id === '/(dialog)');
    },
    false,
    { equals: false }
  );
  const initIsDialog = isDialog();

  const defaultStack = createStackState(
    snapeLocation(
      initIsDialog
        ? {
            pathname: '/',
            query: {},
            state: {},
            key: String(Date.now()),
            search: '',
            hash: '',
          }
        : location
    ),
    {
      children: props.baseRoutes,
      matches: initIsDialog ? props.baseRoutes : matches(),
    },
    false
  );

  const [stacks, setStacks] = createSignal<StackProps[]>([]);

  const [pause, close] = useClose(isDialog);

  /**
   * 这个重定向发生在路由变化前，所以不会触发请求
   * 忽略第一次调用
   * 第一次调用使用query里面的redirect来判断重定向
   */
  let beforeRoute = (_match: RouteDefinition, _location: Location): string | false => {
    beforeRoute = props.beforeRoute;
    return false;
  };

  const macheSet = createMacheSet(
    createMemo<RouteMatch[]>((pre) => {
      if (pause()) {
        return pre;
      } else {
        const matchList = matches();
        // 保存第一次引用, 这行代码并不多余
        const beforeCheck = beforeRoute;
        for (const match of matchList) {
          const redirect = untrack(() => beforeCheck(match.route.key as RouteDefinition, location));
          if (redirect) {
            navigate(redirect, { replace: true });
            return pre;
          }
        }
        return matchList;
      }
    }, matches())
  );

  const action = createMemo(() => macheSet() && getAction());

  if (!import.meta.env.SSR) {
    if (initIsDialog) {
      initHistory(location);
    }
  }

  const currentStack = createMemo<StackProps>((preStack) => {
    const matchSet = macheSet();
    // const matches = [...matchSet.values()];
    if (matchSet.size === 0) return preStack;
    return untrack(() => {
      const dialog = isDialog();
      const matchesList = [...matchSet.values()];
      const stackChildren = dialog ? matchesList.slice(0, 1) : baseRoutes;
      const act = action();
      const snap = snapeLocation(location);
      let stack: StackProps | undefined;
      const list = stacks();
      const preIndex = list.lastIndexOf(preStack);

      if (act === 'REPLACE') {
        if (dialog && !preStack.dialog) {
          const clone = list.concat();
          stack = createStackState(
            snap,
            {
              children: stackChildren,
              matches: matchesList,
            },
            dialog
          );
          clone.splice(preIndex + 1, 0, stack);
          setStacks(clone);
        } else if (matchSet.has(preStack.children[0])) {
          // 检查真正的业务路由（忽略布局路由 /）是否相同
          // 如果不同，需要创建新 stack；如果相同，只更新状态
          const oldValidMatch = preStack.matches.slice(1).find((m) => m.path !== '/');
          const newValidMatch = [...matchSet].slice(1).find((m) => m.path !== '/');

          if (oldValidMatch === newValidMatch) {
            // 在同一个 dialog 内切换（例如 /login → /verify）
            preStack.setState(snap);
            stack = preStack;
          } else {
            // 切换到不同的 dialog 页面（例如 /login → /deposit）
            const clone = list.concat();
            stack = createStackState(
              snap,
              {
                children: stackChildren,
                matches: matchesList,
              },
              dialog
            );
            clone.splice(preIndex, 1, stack);
            setStacks(clone);
          }
        } else {
          const clone = list.concat();
          stack = createStackState(
            snap,
            {
              children: stackChildren,
              matches: matchesList,
            },
            dialog
          );
          clone.splice(preIndex, 1, stack);

          setStacks(clone);
        }
      } else if (act === 'POP') {
        const back = list[preIndex - 1];
        if (back && isSameStack(back, snap)) {
          stack = back;
          stack.setState(snap);
        } else {
          stack = createStackState(
            snap,
            {
              children: stackChildren,
              matches: matchesList,
            },
            dialog
          );
          const clone = list.concat();
          clone.splice(preIndex, 0, stack);
          setStacks(clone);
        }
      } else {
        const forward = list[preIndex + 1];
        if (forward && isSameStack(forward, snap)) {
          stack = forward;
          stack.setState(snap);
        } else {
          stack = createStackState(
            snap,
            {
              children: stackChildren,
              matches: matchesList,
            },
            dialog
          );
          const end = preIndex + 1;
          const first = Math.max(end - MAX_CACHE, 0);
          const clone = list.slice(first, end);
          clone.push(stack);
          setStacks(clone);
        }
      }

      return stack;
    });
  }, defaultStack);

  const index = createMemo(() => stacks().indexOf(currentStack()));
  // 同步location
  createEffect(() => {
    const stack = currentStack();
    // console.log(stack);
    const snap = snapeLocation(location);
    if (stack.pathname === location.pathname) {
      stack.setState(snap);
    }
  });

  const ctx = {
    get index() {
      return index();
    },
    root: defaultStack,
    get stacks() {
      return stacks();
    },
    get current() {
      return currentStack();
    },
    get action() {
      return action();
    },
    close,
  };

  return <Context.Provider value={ctx}>{props.children}</Context.Provider>;
}

function useClose(isDialog: Accessor<boolean>) {
  const navigate = useNavigate();
  const [pause, setPause] = createSignal(false);

  const cbs: VoidFunction[] = [];
  createComputed(() => {
    if (pause()) {
      if (isDialog()) {
        navigate(-1);
      } else {
        setPause(false);
        while (cbs.length) {
          const cb = cbs.shift();
          cb instanceof Function && cb();
        }
      }
    }
  });

  return [
    pause,
    (cb?: VoidFunction) => {
      setPause(true);
      cb && cbs.push(cb);
    },
  ] as const;
}

function isSameStack(a: LocationItem, b: LocationItem) {
  return a.pathname === b.pathname && a.state._depth === b.state._depth;
}

function createMacheSet(matches: () => RouteMatch[]) {
  const matchSet = new Set<RouteDefinition>();
  const isRouting = useIsRouting();
  const mcs = createMemo<RouteMatch[]>((pre) => {
    if (isRouting()) {
      return pre;
    }
    return matches();
  }, matches());
  return createMemo(
    () => {
      matchSet.clear();
      mcs().forEach((it) => {
        matchSet.add(it.route.key as any);
      });
      return matchSet;
    },
    matchSet,
    { equals: false }
  );
}

function createStackState(
  location: LocationItem,
  props: {
    children: RouteDefinition[];
    matches: RouteDefinition[];
  },
  dialog: boolean = true
): StackProps {
  return untrack(() => {
    const [url, setSearch] = createSignal(location.url);
    const [state, setState] = createSignal(location.state);
    const { children, matches } = props;

    const stack: StackProps = {
      get url() {
        return url();
      },
      get state() {
        return state();
      },
      dialog,
      key: dialog ? matches[matches.length - 1] || children[children.length - 1] || defaultKey : defaultKey,
      pathname: location.pathname,
      children,
      matches,
      setState(props) {
        const { url: url2, state: newState, pathname } = untrack(() => ({ ...props, state: { ...props.state } }));
        stack.pathname = pathname;
        batch(() => {
          untrack(() => {
            if (url2 !== url()) {
              setSearch(url2);
            }
            if (!shallow(newState, state())) {
              setState(newState);
            }
          });
        });
      },
    };
    return stack;
  });
}

const SESSION_LENGTH = 'init_length';
// const ROOT_NAME = 'root_path';

// let initPosition = -1;
/** 防止history.length超过50导致action判断异常 */
// function syncRoot(location: Location, act: Action) {
//   if (location.pathname === '/') {
//     if (initPosition === -1) {
//       const cache = sessionStorage.getItem(ROOT_NAME);
//       if (cache) {
//         initPosition = Number(cache);
//       } else {
//         initPosition = history.length;
//         sessionStorage.setItem(ROOT_NAME, String(initPosition));
//       }
//     }
//     if (history.length > 30 && act === 'PUSH') {
//       history.go(initPosition - history.length);
//       return true;
//     }
//   }
//   return false;
// }

function initHistory(location: Location) {
  const init = sessionStorage.getItem(SESSION_LENGTH);
  let length: number;
  const historyLength = history.length;
  if (!init) {
    length = historyLength;
    sessionStorage.setItem(SESSION_LENGTH, String(historyLength));
  } else {
    length = Number(init);
  }
  if (length === historyLength) {
    const _depth = history.state?._depth || history.length - 1;
    history.replaceState({ _depth }, '', '/');
    history.pushState({ _depth: _depth + 1 }, '', location.pathname + location.search);
  }
}

export interface DialogConfig {
  title?: JSX.Element;
  class?: string;
  /** 追加在 Portal 根节点（`.dialog-root`）上，用于提高整块弹窗遮罩相对页面的 z-index */
  portalClass?: string;
  /**
   * 覆盖 `dialog-item` 的 z-index。默认由路由栈转场写死为 1/2，会盖掉 `class` 里的 z-*。
   */
  itemZIndex?: number | string;
  background?: JSX.Element;
  titleColor?: string;
  backgroundColor?: string;
}

export interface RouteDefinition extends BaseRouteDefinition {
  info?: {
    login?: boolean;
    dialog?: DialogConfig;
  };
}
